/**
 * Shared list-controls types. Used by both `/work-items` and `/documents` (and
 * any future list page that adopts the same toolbar grammar). Both pages store
 * facet selections as `Record<facetKey, string[]>` and expose group-by + column
 * picker state through the same primitive components.
 */

export type FilterDict = Record<string, string[]>;

export interface FacetOption {
  id: string;
  label: string;
}

export interface FacetDef {
  /** Stable identifier; also the URL param name on the host page. */
  key: string;
  /** Human label shown in the + Filter popover and the active chip. */
  label: string;
  /** Multi-select values for this facet. */
  options: ReadonlyArray<FacetOption>;
  /**
   * When true the facet shows in the + Filter list as a non-clickable
   * "Coming soon" row — visible so users see the roadmap, but the value pane
   * + URL state are skipped until the underlying property ships.
   */
  comingSoon?: boolean;
}

export interface GroupByOption {
  /** Stable identifier; matches the URL `?groupBy=` value. */
  key: string;
  /** Label shown in the dropdown row and in the trigger button. */
  label: string;
}

export interface PropertyChoice {
  /** Stable identifier; round-trips in the URL `?cols=` param. */
  key: string;
  label: string;
}

/**
 * Strip empty arrays from a `FilterDict`. Used by URL encoders so a facet with
 * zero selected values doesn't emit an empty `?foo=` param.
 */
export function nonEmptyFacetKeys(filters: FilterDict): string[] {
  return Object.keys(filters).filter((k) => filters[k] && filters[k].length > 0);
}
